"use client";

import { useEffect } from "react";
import {
  clientObservabilityEndpoint as endpoint,
  errorKind,
  OBSERVABILITY_PATH,
  recordClientEvent,
} from "@/lib/client-observability";

function normalizeUrl(url: unknown) {
  if (typeof window === "undefined") return null;
  const value = String(url || "");
  if (!value) return null;

  try {
    return new URL(value, window.location.origin);
  } catch {
    return null;
  }
}

function allowedApiOrigins() {
  if (typeof window === "undefined") return new Set<string>();
  const origins = new Set<string>([window.location.origin]);
  const apiUrl = normalizeUrl(endpoint());
  if (apiUrl) origins.add(apiUrl.origin);
  return origins;
}

function shouldRecordClientApiFailure(url: unknown) {
  const parsedUrl = normalizeUrl(url);
  if (!parsedUrl) return false;
  if (parsedUrl.pathname === OBSERVABILITY_PATH) return false;
  if (parsedUrl.pathname === "/form-submit") return parsedUrl.origin === window.location.origin;
  if (!parsedUrl.pathname.startsWith("/api/")) return false;
  return allowedApiOrigins().has(parsedUrl.origin);
}

function classifyEndpoint(url: unknown) {
  if (!shouldRecordClientApiFailure(url)) return null;
  const parsedUrl = normalizeUrl(url);
  const pathname = parsedUrl?.pathname || "";
  if (pathname.startsWith("/api/uploads") || pathname.startsWith("/api/transcribe")) return "upload";
  if (pathname === "/form-submit") return "feedback";
  return "other";
}

function ratingForMetric(name: string, value: number) {
  if (name === "CLS") {
    if (value <= 0.1) return "good";
    if (value <= 0.25) return "needs_improvement";
    return "poor";
  }
  if (name === "LCP") {
    if (value <= 2.5) return "good";
    if (value <= 4) return "needs_improvement";
    return "poor";
  }
  if (name === "INP") {
    if (value <= 0.2) return "good";
    if (value <= 0.5) return "needs_improvement";
    return "poor";
  }
  return value <= 1.8 ? "good" : value <= 3 ? "needs_improvement" : "poor";
}

function observePerformance() {
  if (typeof PerformanceObserver === "undefined") return;
  const observe = (type: string, handler: (entry: PerformanceEntry) => void) => {
    try {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach(handler);
      });
      observer.observe({ type, buffered: true });
    } catch {
      // Unsupported entry type in this browser.
    }
  };

  observe("largest-contentful-paint", (entry) => {
    const value = Math.max(0, entry.startTime / 1000);
    const rating = ratingForMetric("LCP", value);
    recordClientEvent({ eventType: "web_vital", outcome: rating, metric: "LCP", value, rating });
  });

  observe("layout-shift", (entry) => {
    const layoutShift = entry as PerformanceEntry & { value?: number; hadRecentInput?: boolean };
    if (layoutShift.hadRecentInput) return;
    const value = Number(layoutShift.value || 0);
    const rating = ratingForMetric("CLS", value);
    recordClientEvent({ eventType: "web_vital", outcome: rating, metric: "CLS", value, rating });
  });

  observe("event", (entry) => {
    if (entry.name !== "click" && entry.name !== "keydown") return;
    const value = Math.max(0, (entry.duration || 0) / 1000);
    const rating = ratingForMetric("INP", value);
    recordClientEvent({ eventType: "web_vital", outcome: rating, metric: "INP", value, rating });
  });
}

export function ClientObservability() {
  useEffect(() => {
    const onError = (event: ErrorEvent) => {
      recordClientEvent({ eventType: "runtime_error", errorKind: errorKind(event.error || event.message) });
    };
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      recordClientEvent({ eventType: "unhandled_rejection", errorKind: errorKind(event.reason) });
    };
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (input, init) => {
      const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input?.url;
      if (String(url || "").includes(OBSERVABILITY_PATH)) {
        return originalFetch(input, init);
      }
      const shouldRecordApiFailure = shouldRecordClientApiFailure(url);
      try {
        const response = await originalFetch(input, init);
        if (shouldRecordApiFailure && !response.ok && response.status >= 400) {
          recordClientEvent({
            eventType: "api_failure",
            endpointKind: classifyEndpoint(url),
            status: response.status,
            errorKind: "http",
          });
        }
        return response;
      } catch (error) {
        if (shouldRecordApiFailure) {
          recordClientEvent({
            eventType: "api_failure",
            endpointKind: classifyEndpoint(url),
            status: 0,
            errorKind: errorKind(error),
          });
        }
        throw error;
      }
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    observePerformance();

    return () => {
      window.fetch = originalFetch;
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  return null;
}
